import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { gridTheme, defaultColDef, centeredCellStyle, centerAlignedCellStyle } from '../services/aggrid';
import { Panel, Chip, Btn, Dot, PageHead, I } from '../components/hud';

axios.defaults.withCredentials = true;

const API_BASE_URL = (import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:5000')) + '/api';

function Logs() {
  const { user } = useOutletContext();
  const canViewLogs = user?.permissions?.canViewMembers;

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const fetchLogs = async () => {
    if (!canViewLogs) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/notifications/logs`, {
        params: { limit: 100 }
      });
      setLogs(response.data);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.response?.data?.error || 'Failed to fetch notification logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [canViewLogs]);

  const handleOpenDetail = (log) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLogTypeInfo = (type) => {
    const types = {
      'event_notification': { label: 'Event Notification', color: 'primary' },
      'fee_reminder': { label: 'Fee Reminder', color: 'warning' },
      'announcement': { label: 'Announcement', color: 'info' }
    };
    return types[type] || { label: type, color: 'default' };
  };

  // Telemetry: derive from real data only
  const sevenDaysAgo = useMemo(() => Date.now() - 7 * 24 * 60 * 60 * 1000, []);
  const recentLogs = useMemo(() => logs.filter(l => new Date(l.createdAt).getTime() >= sevenDaysAgo), [logs, sevenDaysAgo]);
  const totalSent = useMemo(() => recentLogs.reduce((s, l) => s + (l.totalSent || 0), 0), [recentLogs]);
  const totalFailed = useMemo(() => recentLogs.reduce((s, l) => s + (l.totalFailed || 0), 0), [recentLogs]);
  const deliveredPct = totalSent > 0 ? Math.round(((totalSent - totalFailed) / totalSent) * 100) : 0;

  const typeChip = (type) => {
    if (type === 'fee_reminder') return <Chip tone="amber">FEE</Chip>;
    if (type === 'announcement') return <Chip tone="magenta">ANNOUNCE</Chip>;
    return <Chip>EVENT</Chip>;
  };

  const statusChip = (totalFailed) => {
    if (!totalFailed || totalFailed === 0) {
      return <Chip tone="ok"><Dot />OK</Chip>;
    }
    return <Chip tone="danger"><Dot tone="danger" />PARTIAL</Chip>;
  };

  const columnDefs = useMemo(() => [
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 200,
      valueGetter: (p) => p.data.createdAt ? new Date(p.data.createdAt) : null,
      filter: 'agDateColumnFilter',
      sort: 'desc',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <span className="mono cell-mute">{formatDate(params.data.createdAt)}</span>
      )
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 200,
      valueGetter: (p) => getLogTypeInfo(p.data.type).label,
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => typeChip(params.data.type)
    },
    {
      headerName: 'Event',
      flex: 2,
      minWidth: 240,
      valueGetter: (p) => p.data.eventName || p.data.title || '',
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <span className="cell-strong">
          {params.data.eventName || (params.data.title ? `"${params.data.title}"` : '-')}
        </span>
      )
    },
    {
      headerName: 'Sent By',
      width: 200,
      valueGetter: (p) => p.data.triggeredBy?.characterName || 'System',
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <span className="cell-mute">{params.data.triggeredBy?.characterName || 'System'}</span>
      )
    },
    {
      field: 'totalSent',
      headerName: 'Sent',
      width: 110,
      filter: 'agNumberColumnFilter',
      cellStyle: centerAlignedCellStyle,
      cellRenderer: (params) => (
        <span className="num" style={{ color: 'var(--ok)' }}>{params.value}</span>
      )
    },
    {
      field: 'totalFailed',
      headerName: 'Failed',
      width: 110,
      filter: 'agNumberColumnFilter',
      cellStyle: centerAlignedCellStyle,
      cellRenderer: (params) => (
        <span className="num" style={{ color: params.value > 0 ? 'var(--danger)' : 'var(--text-3)' }}>
          {params.value}
        </span>
      )
    },
    {
      headerName: 'Status',
      width: 130,
      sortable: false,
      filter: false,
      cellStyle: centerAlignedCellStyle,
      cellRenderer: (params) => statusChip(params.data.totalFailed)
    },
    {
      headerName: 'Actions',
      width: 110,
      sortable: false,
      filter: false,
      cellStyle: centerAlignedCellStyle,
      cellRenderer: (params) => (
        <Button size="small" onClick={(e) => { e.stopPropagation(); handleOpenDetail(params.data); }}>
          Details
        </Button>
      )
    }
  ], []);

  if (!canViewLogs) {
    return (
      <>
        <PageHead crumb="PERSONNEL // BROADCAST LOG" title="Notification Logs" />
        <Panel title="Access Denied">
          <p className="cell-mute">You do not have permission to view notification logs.</p>
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHead
        crumb="PERSONNEL // BROADCAST LOG"
        title="Notification Logs"
        actions={
          <Btn icon={I.Sync} onClick={fetchLogs}>Refresh</Btn>
        }
      />

      <div className="telemetry-strip">
        <div className="tile">
          <div className="k">SENT · 7 DAYS</div>
          <div className="v">{totalSent}<span className="unit">PINGS</span></div>
        </div>
        <div className="tile ok">
          <div className="k">DELIVERED</div>
          <div className="v">{deliveredPct}<span className="unit">%</span></div>
        </div>
        <div className="tile amber">
          <div className="k">FAILED</div>
          <div className="v">{totalFailed}<span className="unit">PINGS</span></div>
        </div>
      </div>

      {error && (
        <div className="panel-wrap" style={{ marginBottom: 8 }}>
          <div className="panel square">
            <div className="panel-inner">
              <span style={{ color: 'var(--danger)' }}>{error}</span>
              <Btn tone="" onClick={() => setError(null)} style={{ marginLeft: 12 }}>Dismiss</Btn>
            </div>
          </div>
        </div>
      )}

      <Panel
        title="Broadcast Log"
        sub={`${logs.length} ENTRIES`}
        action={<Chip tone="ok"><Dot tone="pulse" />LIVE STREAM</Chip>}
      >
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)' }}>
            Loading…
          </div>
        ) : logs.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)' }}>
            No notification logs found.
          </div>
        ) : (
          <div className="hud-grid">
            <AgGridReact
              theme={gridTheme}
              rowData={logs}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              domLayout="autoHeight"
              rowHeight={48}
              headerHeight={40}
              floatingFiltersHeight={36}
              pagination
              paginationPageSize={25}
              paginationPageSizeSelector={[10, 25, 50, 100]}
              onRowClicked={(e) => handleOpenDetail(e.data)}
              getRowId={(params) => params.data._id}
              animateRows
            />
          </div>
        )}
      </Panel>

      {/* Detail Modal — kept as MUI Dialog (functional/risky) */}
      <Dialog open={detailModalOpen} onClose={() => setDetailModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Notification Details
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <div>
              <div className="grid-2" style={{ marginBottom: 16 }}>
                <div>
                  <div className="label">Date</div>
                  <span className="mono">{formatDate(selectedLog.createdAt)}</span>
                </div>
                <div>
                  <div className="label">Type</div>
                  {typeChip(selectedLog.type)}
                </div>
                <div>
                  <div className="label">Event</div>
                  <span className="cell-strong">{selectedLog.eventName || '-'}</span>
                </div>
                <div>
                  <div className="label">Sent By</div>
                  <span>{selectedLog.triggeredBy?.characterName || 'System'}</span>
                </div>
                {selectedLog.title && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <div className="label">Title</div>
                    <span className="cell-strong">{selectedLog.title}</span>
                  </div>
                )}
                {selectedLog.message && (
                  <div style={{ gridColumn: 'span 2' }}>
                    <div className="label" style={{ marginBottom: 6 }}>Message Content</div>
                    <div style={{
                      padding: '10px 14px',
                      background: 'var(--bg-2)',
                      border: '1px solid var(--border)',
                      whiteSpace: 'pre-wrap',
                      fontSize: 13,
                      color: 'var(--text-1)'
                    }}>
                      {selectedLog.message}
                    </div>
                  </div>
                )}
              </div>

              <div className="divlabel">Recipients ({selectedLog.recipients?.length || 0})</div>

              {selectedLog.recipients?.length > 0 ? (
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }} className="hud-table">
                    <thead>
                      <tr>
                        <th>Character</th>
                        <th>Discord</th>
                        <th style={{ textAlign: 'center' }}>Status</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedLog.recipients.map((recipient, index) => (
                        <tr key={index}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Avatar
                                src={`https://images.evetech.net/characters/${recipient.characterId}/portrait?size=32`}
                                alt={recipient.characterName}
                                sx={{ width: 24, height: 24 }}
                              />
                              <span className="cell-strong">{recipient.characterName}</span>
                            </div>
                          </td>
                          <td className="cell-mute">{recipient.discordUsername || '-'}</td>
                          <td style={{ textAlign: 'center' }}>
                            {recipient.success
                              ? <Chip tone="ok"><Dot />OK</Chip>
                              : <Chip tone="danger"><Dot tone="danger" />FAIL</Chip>
                            }
                          </td>
                          <td>
                            {recipient.error && (
                              <span style={{ color: 'var(--danger)', fontSize: 12 }}>{recipient.error}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="cell-mute">No recipients recorded.</p>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Logs;
